import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout/PageLayout';
import { outfitService, Outfit, OutfitComment } from '../services/outfit';
import { Button } from '../components/common/Button';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { getImageUrl } from '../utils/imageUrl';
import { storage } from '../config/firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';

const OCCASION_OPTIONS = [
  'Wedding',
  'Formal event',
  'Date night',
  'Thanksgiving',
  'Christmas',
  'Family photos',
  'Office Outfit',
  'Everyday Casual',
  'Other'
];

interface OutfitWithDetails extends Outfit {
  user_style_bio?: string;
  average_rating?: number;
  comment_count?: number;
}

export const Outfits: React.FC = () => {
  const navigate = useNavigate();
  const [outfits, setOutfits] = useState<OutfitWithDetails[]>([]);
  const [filteredOutfits, setFilteredOutfits] = useState<OutfitWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolvedPreviewUrls, setResolvedPreviewUrls] = useState<{ [outfitId: string]: string }>({});
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; outfitId: string | null; outfitName: string }>({
    isOpen: false,
    outfitId: null,
    outfitName: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [occasionFilter, setOccasionFilter] = useState<string>('');
  const [commentModal, setCommentModal] = useState<{ isOpen: boolean; outfitId: string | null }>({
    isOpen: false,
    outfitId: null,
  });
  const [comments, setComments] = useState<{ [outfitId: string]: OutfitComment[] }>({});
  const [commentCounts, setCommentCounts] = useState<{ [outfitId: string]: number }>({});
  const [averageRatings, setAverageRatings] = useState<{ [outfitId: string]: number }>({});

  const filterOutfits = useCallback(() => {
    let filtered = [...outfits];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((outfit) => {
        // Search by outfit name
        if (outfit.name?.toLowerCase().includes(query)) return true;
        
        // Search by occasion
        if (outfit.occasion?.toLowerCase().includes(query)) return true;
        
        // Search by clothing metadata (category, color, brand)
        const matchesMetadata = outfit.items?.some((item) => {
          return (
            item.category?.toLowerCase().includes(query) ||
            item.subcategory?.toLowerCase().includes(query) ||
            item.color?.toLowerCase().includes(query) ||
            item.brand?.toLowerCase().includes(query)
          );
        });
        
        return matchesMetadata;
      });
    }
    
    // Occasion filter
    if (occasionFilter) {
      filtered = filtered.filter((outfit) => outfit.occasion === occasionFilter);
    }
    
    setFilteredOutfits(filtered);
  }, [outfits, searchQuery, occasionFilter]);

  useEffect(() => {
    loadOutfits();
  }, []);

  useEffect(() => {
    filterOutfits();
  }, [filterOutfits]);

  useEffect(() => {
    // Load comment counts and average ratings for all outfits when outfits change
    if (outfits.length > 0) {
      loadCommentCounts();
      loadAverageRatings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outfits.length]);

  const loadCommentCounts = async () => {
    const counts: { [outfitId: string]: number } = {};
    try {
      await Promise.all(
        outfits.map(async (outfit) => {
          try {
            const outfitComments = await outfitService.getComments(outfit.id);
            counts[outfit.id] = outfitComments.length;
            console.log(`Loaded ${outfitComments.length} comments for outfit ${outfit.id}`);
          } catch (err) {
            console.error(`Failed to load comment count for outfit ${outfit.id}:`, err);
            counts[outfit.id] = 0;
          }
        })
      );
      console.log('Comment counts loaded:', counts);
      setCommentCounts(counts);
    } catch (err) {
      console.error('Error loading comment counts:', err);
    }
  };

  const loadAverageRatings = async () => {
    const ratings: { [outfitId: string]: number } = {};
    try {
      await Promise.all(
        outfits.map(async (outfit) => {
          try {
            const avgRating = await outfitService.getAverageRating(outfit.id);
            ratings[outfit.id] = avgRating;
            console.log(`Loaded average rating ${avgRating.toFixed(2)} for outfit ${outfit.id}`);
          } catch (err) {
            console.error(`Failed to load average rating for outfit ${outfit.id}:`, err);
            ratings[outfit.id] = 0;
          }
        })
      );
      console.log('Average ratings loaded:', ratings);
      setAverageRatings(ratings);
    } catch (err) {
      console.error('Error loading average ratings:', err);
    }
  };

  const loadOutfits = async () => {
    try {
      console.log('Loading outfits...');
      const data = await outfitService.getAll();
      console.log('Loaded outfits:', data);
      
      // Ensure items array exists for each outfit
      const normalizedData = (data || []).map((outfit) => ({
        ...outfit,
        items: outfit.items || [],
      }));
      
      console.log('Normalized outfits:', normalizedData);
      
      // Load user data for style bio
      const outfitsWithUserData = await Promise.all(
        normalizedData.map(async (outfit) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', outfit.user_id));
            const userData = userDoc.data();
            return {
              ...outfit,
              user_style_bio: userData?.public_style_bio || '',
            };
          } catch (error) {
            console.error(`Failed to load user data for outfit ${outfit.id}:`, error);
            return {
              ...outfit,
              user_style_bio: '',
            };
          }
        })
      );
      
      setOutfits(outfitsWithUserData);
      
      // Resolve preview URLs for outfits that have them
      const previewUrlMap: { [outfitId: string]: string } = {};
      await Promise.all(
        outfitsWithUserData.map(async (outfit) => {
          if (outfit.preview_url) {
            try {
              // Extract file path from URL
              let filePath: string | null = null;
              
              if (outfit.preview_url.includes('/o/')) {
                // Emulator or Firebase Storage URL format
                const match = outfit.preview_url.match(/\/o\/([^?]+)/);
                if (match) {
                  filePath = decodeURIComponent(match[1]);
                }
              } else if (outfit.preview_url.includes('storage.googleapis.com')) {
                // Production URL format: https://storage.googleapis.com/bucket/path
                const match = outfit.preview_url.match(/storage\.googleapis\.com\/[^/]+\/(.+)/);
                if (match) {
                  filePath = match[1];
                }
              }
              
              if (filePath) {
                console.log(`Resolving preview URL for outfit ${outfit.id}:`, filePath);
                const storageRef = ref(storage, filePath);
                const downloadURL = await getDownloadURL(storageRef);
                previewUrlMap[outfit.id] = downloadURL;
                console.log(`Resolved preview URL for outfit ${outfit.id}:`, downloadURL);
              } else {
                // Fallback to original URL
                previewUrlMap[outfit.id] = outfit.preview_url;
              }
            } catch (urlError) {
              console.error(`Failed to resolve preview URL for outfit ${outfit.id}:`, urlError);
              // Fallback to original URL
              previewUrlMap[outfit.id] = outfit.preview_url || '';
            }
          }
        })
      );
      
      setResolvedPreviewUrls(previewUrlMap);
    } catch (err: any) {
      console.error('Failed to load outfits:', err);
      setOutfits([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteConfirm({
      isOpen: true,
      outfitId: id,
      outfitName: name,
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.outfitId) {
      try {
        await outfitService.delete(deleteConfirm.outfitId);
        setOutfits(outfits.filter(outfit => outfit.id !== deleteConfirm.outfitId));
        setFilteredOutfits(filteredOutfits.filter(outfit => outfit.id !== deleteConfirm.outfitId));
      } catch (err) {
        console.error('Failed to delete outfit:', err);
      }
    }
    setDeleteConfirm({ isOpen: false, outfitId: null, outfitName: '' });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, outfitId: null, outfitName: '' });
  };

  const handleCommentClick = async (outfitId: string) => {
    setCommentModal({ isOpen: true, outfitId });
    
    // Always reload comments to get latest
    try {
      const loadedComments = await outfitService.getComments(outfitId);
      setComments((prev) => ({ ...prev, [outfitId]: loadedComments }));
      setCommentCounts((prev) => ({ ...prev, [outfitId]: loadedComments.length }));
      console.log(`Loaded ${loadedComments.length} comments for outfit ${outfitId}`);
    } catch (err) {
      console.error('Failed to load comments:', err);
      setComments((prev) => ({ ...prev, [outfitId]: [] }));
      setCommentCounts((prev) => ({ ...prev, [outfitId]: 0 }));
    }
  };

  const handleCommentModalClose = () => {
    // Refresh comment counts when modal closes in case comments were added
    if (commentModal.outfitId) {
      loadCommentCounts();
    }
    setCommentModal({ isOpen: false, outfitId: null });
  };

  const getAverageRating = (outfitId: string): number => {
    return averageRatings[outfitId] || 0;
  };

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">My Outfits</h1>
        
        {/* Search and Filter */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search Outfits"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          <div className="min-w-[200px]">
            <select
              value={occasionFilter}
              onChange={(e) => setOccasionFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All Occasions</option>
              {OCCASION_OPTIONS.map((occasion) => (
                <option key={occasion} value={occasion}>
                  {occasion}
                </option>
              ))}
            </select>
          </div>
          
          <Button variant="primary" size="md" onClick={() => navigate('/outfits/create')}>
            Create an Outfit
          </Button>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading...</p>
          </div>
        ) : filteredOutfits.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {outfits.length === 0
                ? "You haven't created any outfits yet!"
                : 'No outfits match your search criteria.'}
            </p>
            {outfits.length === 0 && (
              <Button onClick={() => navigate('/outfits/create')}>Create Your First Outfit</Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredOutfits.map((outfit) => {
              const items = outfit.items || [];
              
              return (
                <div
                  key={outfit.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow relative"
                >
                  {/* Edit icon button on top right */}
                  <button
                    onClick={() => navigate(`/outfits/${outfit.id}/edit`)}
                    className="absolute top-4 right-4 p-2 bg-white/80 hover:bg-white rounded-full shadow-sm transition-colors z-10"
                    aria-label="Edit outfit"
                  >
                    <svg
                      className="w-5 h-5 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                  
                  <div className="p-4">
                    {/* Outfit Name */}
                    <h3 className="text-xl font-bold mb-3 pr-8">{outfit.name || 'Unnamed Outfit'}</h3>
                    
                    {/* Outfit Image */}
                    {outfit.preview_url ? (
                      <div className="aspect-[3/4] rounded-xl overflow-hidden mb-4 bg-gray-100">
                        <img
                          src={resolvedPreviewUrls[outfit.id] || getImageUrl(outfit.preview_url)}
                          alt={outfit.name || 'Outfit preview'}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            console.error(`Failed to load preview for outfit ${outfit.id}:`, outfit.preview_url);
                            console.error(`Resolved URL:`, resolvedPreviewUrls[outfit.id]);
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log(`Preview loaded successfully for outfit ${outfit.id}`);
                          }}
                        />
                      </div>
                    ) : items.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 text-sm mb-4">
                        No items in this outfit
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {items.map((item) => {
                          if (!item || !item.id) {
                            console.warn('Invalid item found:', item);
                            return null;
                          }
                          return (
                            <div key={item.id} className="aspect-square rounded-xl overflow-hidden">
                              <img
                                src={getImageUrl(item.image_url)}
                                alt={item.category || 'Clothing item'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  console.error(`Failed to load image for item ${item.id}:`, item.image_url);
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* User's public style bio */}
                    {outfit.user_style_bio && (
                      <p className="text-sm text-gray-700 mb-3">{outfit.user_style_bio}</p>
                    )}
                    
                    {/* Occasion */}
                    {outfit.occasion && (
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {outfit.occasion}
                        </span>
                      </div>
                    )}
                    
                    {/* Rating hearts and comment icon */}
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((rating) => {
                        const avgRating = getAverageRating(outfit.id);
                        const isFilled = rating <= Math.round(avgRating);
                        return (
                          <span
                            key={rating}
                            className={`${
                              isFilled
                                ? 'text-red-500'
                                : 'text-gray-300'
                            }`}
                          >
                            <svg
                              className="w-5 h-5"
                              fill={isFilled ? 'currentColor' : 'none'}
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                              />
                            </svg>
                          </span>
                        );
                      })}
                      
                      {/* Comment icon */}
                      <div className="ml-2 relative inline-block">
                        <button
                          onClick={() => handleCommentClick(outfit.id)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label="View comments"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                          </svg>
                        </button>
                        {commentCounts[outfit.id] !== undefined && commentCounts[outfit.id] > 0 && (
                          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold z-10 border-2 border-white">
                            {commentCounts[outfit.id]}
                          </span>
                        )}
                      </div>
                    </div>
                    
                  </div>
                  
                  {/* Delete text at bottom */}
                  <div className="border-t border-gray-200 p-4">
                    <button
                      onClick={() => handleDeleteClick(outfit.id, outfit.name)}
                      className="w-full text-center text-red-500 hover:text-red-700 text-sm font-medium transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        title="Delete Outfit"
        message={`Are you sure you want to delete "${deleteConfirm.outfitName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Comments Modal */}
      {commentModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Comments</h3>
            <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
              {comments[commentModal.outfitId || '']?.length > 0 ? (
                comments[commentModal.outfitId || '']?.map((comment) => (
                  <div key={comment.id} className="border-b border-gray-200 pb-3">
                    <p className="text-sm text-gray-700">{comment.comment}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm">No comments yet.</p>
              )}
            </div>
            <button
              onClick={handleCommentModalClose}
              className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </PageLayout>
  );
};
